import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { adminLogin } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/site";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user.role === "admin") {
    const dest = location.state?.from || "/admin";
    return <Navigate to={dest} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await adminLogin(form);
      login(res.user);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--trj-navy)] flex items-center justify-center px-6 py-16 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full" style={{background:"radial-gradient(closest-side, rgba(212,175,55,0.35), transparent)"}}/>
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8" data-testid="admin-login-back-home">
          <img src={SITE.assets.logo} alt="TRJ Services" className="h-14 bg-white p-2" />
        </Link>

        <div className="bg-white p-8 md:p-10 border-t-4 border-[var(--trj-gold)] shadow-2xl">
          <div className="trj-eyebrow !text-[var(--trj-green)] mb-2">Secure Admin Access</div>
          <h1 className="font-serif-trj text-3xl text-[var(--trj-navy)] leading-tight">Sign in to your dashboard</h1>
          <p className="text-sm text-[var(--trj-muted)] mt-2">
            Manage enrollments, inquiries, and resource leads.
          </p>
          <div className="trj-divider-gold mt-5" />

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-email" className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Email</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 text-[var(--trj-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="admin-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                  className="rounded-none pl-9"
                  data-testid="admin-login-email"
                  placeholder="you@trjsvc.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Password</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 text-[var(--trj-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="admin-password"
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                  className="rounded-none pl-9"
                  data-testid="admin-login-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="trj-btn-green w-full justify-center disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-7 flex items-center justify-center gap-2 text-[11px] text-[var(--trj-muted)]">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--trj-green)]" />
            Encrypted session · 8-hour token lifetime
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-white/50">
          <Link to="/" className="hover:text-white">← Back to TRJ Services</Link>
        </div>
      </div>
    </div>
  );
}
